import { useTranslation } from "react-i18next";
import { Release, ReleasePricingInfo, ReleasePricingType } from "../../types";
import { Flex, Text } from "@chakra-ui/react";
import { useReleasePricingInfo } from "../../hooks/useReleasePricingInfo";
import { PriceAndStrikePriceLabel } from "./components/PriceAndStrikePriceLabel";
import { YouPurchasedThisLabel } from "./components/YouPurchasedThisLabel";

export interface ReleasePriceLabelProps {
  release?: Release;
  pricingInfo?: ReleasePricingInfo;
  compact?: boolean;
  hideFreeWithGold?: boolean;
  fontSize?: string | Record<string, string> | number;
  showPurchasedLabel?: boolean;
  hideAvailableDiscounts?: boolean;
  displayAsAColumn?: boolean;
  showIncludedLabel?: boolean;
}

export function ReleasePriceLabel({
  pricingInfo,
  release,
  compact = false,
  hideFreeWithGold = false,
  fontSize,
  showPurchasedLabel = false,
  hideAvailableDiscounts = false,
  displayAsAColumn = false,
  showIncludedLabel = false,
}: ReleasePriceLabelProps) {
  const releasePricingInfo = useReleasePricingInfo(release);

  pricingInfo = pricingInfo || releasePricingInfo;

  if (!release && !pricingInfo) {
    return null;
  }

  return renderPricingInfo(
    pricingInfo,
    compact,
    hideFreeWithGold,
    fontSize,
    showPurchasedLabel,
    hideAvailableDiscounts,
    displayAsAColumn,
    showIncludedLabel,
  );
}

function UnlockWithGoldLabel({
  fontSize,
}: {
  fontSize?: string | Record<string, string> | number;
}) {
  const { t } = useTranslation();

  return (
    <Text variant="releasePriceLabel" fontSize={fontSize} whiteSpace="nowrap">
      {t("components.cards.unlockReleaseCard.freeWithGold")}
    </Text>
  );
}

function renderPricingInfo(
  pricingInfo: ReleasePricingInfo,
  compact: boolean,
  hideFreeWithGold: boolean,
  fontSize?: string | Record<string, string> | number,
  showPurchasedLabel?: boolean,
  hideAvailableDiscounts = false,
  displayAsAColumn = false,
  showIncludedLabel = false,
) {
  const { type, regularPrice, goldPrice, salePrice, goldSalePrice } =
    pricingInfo;

  switch (type) {
    case ReleasePricingType.Free:
    case ReleasePricingType.UnlockedWithGold:
      return null;

    case ReleasePricingType.Purchased:
      if (!showPurchasedLabel) return null;
      return <YouPurchasedThisLabel compact={compact} fontSize={fontSize} />;

    case ReleasePricingType.ClaimableGoldMonthly:
      // Monthly reward pricing: $0.00 in success green next to the
      // strikethrough retail price.
      return (
        <Flex
          flexDirection={displayAsAColumn ? "column" : "row"}
          gap={displayAsAColumn ? "0" : "6px"}
          alignItems={displayAsAColumn ? "flex-start" : "center"}
        >
          <Text variant="releasePriceLabel" color="success.500" fontSize={fontSize}>
            $0.00
          </Text>
          {regularPrice != null && (
            <Text
              variant="releasePriceLabel"
              color="white"
              fontSize={fontSize}
              textDecoration="line-through"
            >
              ${regularPrice}
            </Text>
          )}
        </Flex>
      );

    case ReleasePricingType.FreeWithGold:
      return hideFreeWithGold ? null : (
        <UnlockWithGoldLabel fontSize={fontSize} />
      );

    case ReleasePricingType.PpbOrFreeWithGold:
      return (
        <Flex
          flexDirection={displayAsAColumn ? "column" : "row"}
          gap={displayAsAColumn ? "0" : "4px"}
          alignItems={displayAsAColumn ? "flex-start" : "center"}
        >
          <Text variant="releasePriceLabel" color="white" fontSize={fontSize}>
            ${regularPrice}
          </Text>
          {!hideAvailableDiscounts && !displayAsAColumn && (
            <Text variant="releasePriceLabel" color="white" fontSize={fontSize}>
              &nbsp;•&nbsp;
            </Text>
          )}
          {!hideAvailableDiscounts && (
            <UnlockWithGoldLabel fontSize={fontSize} />
          )}
        </Flex>
      );

    case ReleasePricingType.OnSaleOrFreeWithGold:
      if (hideAvailableDiscounts) {
        return (
          <PriceAndStrikePriceLabel
            strokePrice={regularPrice}
            isOnSale
            fontSize={fontSize}
            displayAsAColumn={displayAsAColumn}
          />
        );
      }

      return (
        <Flex
          flexDirection={displayAsAColumn ? "column" : "row"}
          gap={displayAsAColumn ? "0" : "4px"}
          alignItems={displayAsAColumn ? "flex-start" : "center"}
        >
          <Text
            variant="releasePriceLabel"
            color="green.500"
            fontSize={fontSize}
          >
            ${salePrice}
          </Text>
          {!displayAsAColumn && (
            <Text variant="releasePriceLabel" color="white" fontSize={fontSize}>
              &nbsp;•&nbsp;
            </Text>
          )}
          <UnlockWithGoldLabel fontSize={fontSize} />
        </Flex>
      );

    case ReleasePricingType.Regular:
      return (
        <Text variant="releasePriceLabel" color="white" fontSize={fontSize}>
          ${regularPrice}
        </Text>
      );

    case ReleasePricingType.OnSale:
    case ReleasePricingType.OnSaleWithGoldDiscount:
    case ReleasePricingType.OnSaleAndAvailableGoldDiscount:
      return (
        <PriceAndStrikePriceLabel
          price={pricingInfo.salePrice}
          strokePrice={pricingInfo.regularPrice}
          isOnSale={true}
          fontSize={fontSize}
          displayAsAColumn={displayAsAColumn}
          showIncludedLabel={showIncludedLabel}
        />
      );

    case ReleasePricingType.GoldDiscount:
      return (
        <PriceAndStrikePriceLabel
          price={pricingInfo.goldPrice}
          strokePrice={pricingInfo.regularPrice}
          fontSize={fontSize}
          displayAsAColumn={displayAsAColumn}
        />
      );

    case ReleasePricingType.AvailableGoldDiscount:
      if (hideAvailableDiscounts) {
        return (
          <Text
            variant="releasePriceLabel"
            fontSize={fontSize}
            color={"white"}
            whiteSpace="nowrap"
          >
            ${regularPrice}
          </Text>
        );
      }

      return (
        <PriceAndGoldPrice
          price={regularPrice}
          goldPrice={goldPrice}
          compact={compact}
          fontSize={fontSize}
          displayAsAColumn={displayAsAColumn}
        />
      );

    case ReleasePricingType.RegularWithAvailableOnGoldSale:
      if (hideAvailableDiscounts) {
        return (
          <Text
            variant="releasePriceLabel"
            fontSize={fontSize}
            color={"white"}
            whiteSpace="nowrap"
          >
            ${regularPrice}
          </Text>
        );
      }

      return (
        <PriceAndGoldPrice
          price={regularPrice}
          goldPrice={goldSalePrice}
          compact={compact}
          fontSize={fontSize}
          displayAsAColumn={displayAsAColumn}
        />
      );

    case ReleasePricingType.OnGoldSale:
      return (
        <PriceAndStrikePriceLabel
          price={pricingInfo.goldSalePrice}
          strokePrice={pricingInfo.regularPrice}
          isOnSale={true}
          fontSize={fontSize}
          displayAsAColumn={displayAsAColumn}
        />
      );

    case ReleasePricingType.onSaleWithAvailableOnGoldSale:
      if (hideAvailableDiscounts) {
        return (
          <PriceAndStrikePriceLabel
            price={salePrice}
            strokePrice={regularPrice}
            isOnSale
            fontSize={fontSize}
            displayAsAColumn={displayAsAColumn}
          />
        );
      }

      return (
        <PriceAndGoldPrice
          price={salePrice}
          goldPrice={goldSalePrice}
          compact={compact}
          fontSize={fontSize}
          isOnSale
          displayAsAColumn={displayAsAColumn}
        />
      );
  }
}

interface PriceAndGoldPriceProps {
  price?: number;
  goldPrice?: number;
  compact?: boolean;
  isOnSale?: boolean;
  fontSize?: string | Record<string, string> | number;
  displayAsAColumn?: boolean;
}

function PriceAndGoldPrice({
  price,
  goldPrice,
  compact = false,
  isOnSale = false,
  fontSize,
  displayAsAColumn = false,
}: PriceAndGoldPriceProps) {
  return (
    <Flex
      flexDirection={displayAsAColumn ? "column" : "row"}
      alignItems={displayAsAColumn ? "flex-start" : "center"}
      gap={displayAsAColumn ? "0" : "4px"}
      whiteSpace="nowrap"
      flexShrink={0}
    >
      <Text
        variant="releasePriceLabel"
        fontSize={fontSize}
        color={isOnSale ? "green.500" : "white"}
        whiteSpace="nowrap"
      >
        ${price}
      </Text>
      {!displayAsAColumn && (
        <Text
          variant="releasePriceLabel"
          color={"white"}
          fontWeight="600"
          fontSize={fontSize}
          whiteSpace="nowrap"
        >
          &nbsp;•&nbsp;
        </Text>
      )}
      <Text
        variant="releasePriceLabel"
        color="blaze.blaze"
        fontSize={fontSize}
        whiteSpace="nowrap"
      >
        ${goldPrice} {compact ? "Gold" : "with Gold"}
      </Text>
    </Flex>
  );
}
